import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Title3,
  Button,
  Spinner,
} from '@fluentui/react-components';
import { ArrowLeft20Regular } from '@fluentui/react-icons';
import { useEvent } from '../../hooks/useEvent';
import { Event, EventCreate } from '../../types/event';
import { ApiErrorInfo } from '../../types/api';
import EventForm from './components/EventForm';

const useStyles = makeStyles({
  container: {
    padding: tokens.spacingHorizontalXL,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalXL,
  },
  backButton: {
    minWidth: 'auto',
  },
  title: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightBold,
  },
  content: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow16,
    padding: tokens.spacingHorizontalXXL,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
  },
});

const EventFormPage: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('id');
  
  const [editingEvent, setEditingEvent] = React.useState<Event | undefined>();
  const [wasSaving, setWasSaving] = React.useState(false);
  const [currentError, setCurrentError] = React.useState<ApiErrorInfo | null>(null);
  const [isLoadingEvent, setIsLoadingEvent] = React.useState(!!eventId);
  const [eventNotFound, setEventNotFound] = React.useState(false);

  const {
    events,
    saveLoading,
    saveError,
    createEvent,
    updateEvent,
    fetchEvents,
  } = useEvent();

  // Sincronizar erro atual com saveError
  React.useEffect(() => {
    if (saveError) {
      setCurrentError(saveError);
    }
  }, [saveError]);

  // Carregar lista de eventos
  React.useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Buscar evento específico quando a lista for carregada
  React.useEffect(() => {
    if (eventId && events && events.length > 0) {
      const event = events.find(e => e.id === Number(eventId));
      if (event) {
        setEditingEvent(event);
        setEventNotFound(false);
      } else {
        console.warn('Evento não encontrado na lista:', eventId);
        setEventNotFound(true);
      }
      setIsLoadingEvent(false);
    } else if (!eventId) {
      setIsLoadingEvent(false);
      setEventNotFound(false);
    }
  }, [eventId, events]);

  const handleSaveEvent = async (eventData: EventCreate) => {
    setWasSaving(true);
    setCurrentError(null);
    try {
      if (editingEvent) {
        await updateEvent(editingEvent.id, eventData);
      } else {
        await createEvent(eventData);
      }
    } catch (error) {
      console.error('Erro ao salvar evento:', error);
    }
  };

  // Navegar de volta quando salvar com sucesso
  React.useEffect(() => {
    if (wasSaving && !saveLoading) {
      if (!saveError) {
        navigate('/events');
      } else {
        setWasSaving(false);
      }
    }
  }, [wasSaving, saveLoading, saveError, navigate]);

  const handleCancel = () => {
    navigate('/events');
  };

  const handleBack = () => {
    navigate('/events');
  };

  if (isLoadingEvent) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Spinner label="Carregando evento..." size="large" />
        </div>
      </div>
    );
  }

  if (eventNotFound) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <Button
            appearance="subtle"
            icon={<ArrowLeft20Regular />}
            onClick={handleBack}
            className={styles.backButton}
          />
          <Title3 className={styles.title}>Evento não encontrado</Title3>
        </div>
        <div className={styles.content}>
          <p>O evento solicitado não foi encontrado.</p>
          <Button onClick={handleBack}>Voltar para lista</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Button
          appearance="subtle"
          icon={<ArrowLeft20Regular />}
          onClick={handleBack}
          className={styles.backButton}
        />
        <Title3 className={styles.title}>
          {editingEvent ? 'Editar Evento' : 'Novo Evento'}
        </Title3>
      </div>

      <div className={styles.content}>
        <EventForm
          event={editingEvent}
          onSave={handleSaveEvent}
          onCancel={handleCancel}
          loading={saveLoading}
          error={currentError}
        />
      </div>
    </div>
  );
};

export default EventFormPage;
